import type { Artifact, Verdict } from '../types.ts';
import { VERDICT_ORDER, clearFilters, isFilterActive, type FeatureFacet, type FilterState, type Pivot } from '../lib/filters.ts';
import { VerdictBadge } from './VerdictBadge.tsx';

const FACET_OPTIONS: { facet: FeatureFacet; label: string }[] = [
  { facet: 'all', label: 'all' },
  { facet: 'compared', label: 'compared' },
  { facet: 'not_compared', label: 'not compared' },
  { facet: 'proposed', label: 'proposed' },
  { facet: 'dropped', label: 'dropped' },
  { facet: 'third_party_evidence', label: '3rd-party evidence' },
];

/**
 * Global filter bar (spec v1.1): free-text search, verdict multi-select, vendor multi-select,
 * compared/not-compared/proposed/dropped facet, result counts, clear-all, and the pivot switch
 * (kept here rather than as a separate control since it shares this bar's layout row and the
 * spec treats "filters + pivot" as one exploration toolbar). Pure controlled component — all
 * state lives in the parent (ArtifactView), which also owns the URL sync.
 */
export function FilterBar({
  artifact,
  filters,
  onChange,
  resultSummary,
}: {
  artifact: Artifact;
  filters: FilterState;
  onChange: (next: FilterState) => void;
  /** e.g. "9 of 16 capabilities shown" — computed by the parent, since what's being counted differs per pivot. */
  resultSummary: string;
}): JSX.Element {
  const active = isFilterActive(filters);

  function toggleVerdict(v: Verdict): void {
    const has = filters.verdicts.includes(v);
    onChange({ ...filters, verdicts: has ? filters.verdicts.filter((x) => x !== v) : [...filters.verdicts, v] });
  }

  function toggleVendor(company: string): void {
    const has = filters.vendors.includes(company);
    onChange({ ...filters, vendors: has ? filters.vendors.filter((x) => x !== company) : [...filters.vendors, company] });
  }

  function setPivot(pivot: Pivot): void {
    onChange({ ...filters, pivot });
  }

  return (
    <div className="panel filter-bar">
      <div className="filter-bar-row">
        <input
          type="search"
          className="filter-search"
          placeholder="Search capabilities, features, aliases, mentions…"
          value={filters.query}
          onChange={(e) => onChange({ ...filters, query: e.target.value })}
          aria-label="Search the artifact"
        />
        <div className="pivot-switch" role="tablist" aria-label="View">
          <button
            className={filters.pivot === 'matrix' ? 'active' : ''}
            aria-pressed={filters.pivot === 'matrix'}
            onClick={() => setPivot('matrix')}
          >
            Matrix
          </button>
          <button
            className={filters.pivot === 'vendor' ? 'active' : ''}
            aria-pressed={filters.pivot === 'vendor'}
            onClick={() => setPivot('vendor')}
          >
            By vendor
          </button>
        </div>
        <button className="btn sm" onClick={() => onChange(clearFilters(filters))} disabled={!active}>
          Clear all
        </button>
      </div>

      <div className="filter-bar-row">
        <div className="filter-group">
          <span className="label filter-group-label">verdict</span>
          {VERDICT_ORDER.map((v) => (
            <button
              key={v}
              className={`chip-toggle${filters.verdicts.includes(v) ? ' active' : ''}`}
              aria-pressed={filters.verdicts.includes(v)}
              onClick={() => toggleVerdict(v)}
            >
              <VerdictBadge verdict={v} />
            </button>
          ))}
        </div>

        <div className="filter-group">
          <span className="label filter-group-label">facet</span>
          {FACET_OPTIONS.map((f) => (
            <button
              key={f.facet}
              className={`chip-toggle${filters.facet === f.facet ? ' active' : ''}`}
              aria-pressed={filters.facet === f.facet}
              onClick={() => onChange({ ...filters, facet: f.facet })}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div className="filter-bar-row">
        <div className="filter-group">
          <span className="label filter-group-label">vendors</span>
          {artifact.vendors.map((v) => (
            <button
              key={v.slug}
              className={`chip-toggle${filters.vendors.includes(v.company) ? ' active' : ''}`}
              aria-pressed={filters.vendors.includes(v.company)}
              onClick={() => toggleVendor(v.company)}
            >
              {v.company}
            </button>
          ))}
          {/* Unassessed competitors are filterable too — they are columns in the matrix, and a
              vendor list that omitted them could not be used to isolate one of them. */}
          {(artifact.degraded?.failed_vendors ?? []).map((v) => (
            <button
              key={v.slug}
              className={`chip-toggle degraded-chip${filters.vendors.includes(v.company) ? ' active' : ''}`}
              aria-pressed={filters.vendors.includes(v.company)}
              onClick={() => toggleVendor(v.company)}
              title={`not assessed — ${v.error}`}
            >
              {v.company}
            </button>
          ))}
        </div>
      </div>

      <p className="filter-result-summary">{resultSummary}</p>
    </div>
  );
}
